import { useCallback, useContext, useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { IChatbotAPIPagination } from '../../commons/interfaces/chatbotAPI.interface';
import { INotification } from '../../commons/interfaces/notification.interface';
import NotificationIcon from '../../components/NotificationIcon';
import NotificationList from '../../components/NotificationList';
import QueryResult from '../../components/QueryResult';
import { SocketContext } from '../../contexts/socket.context';
import ManagerAPI from '../../services/manager.service';
import NotificationService from '../../services/notification.service';

interface NotificationsProps {
  userId: string | undefined;
}

const notificationService = new NotificationService();
const managerService = new ManagerAPI();

const Notifications = ({ userId }: NotificationsProps) => {
  const initialPagination = { page: 1, pageSize: 20, pageCount: -1, totalCount: -1 };
  const navigate = useNavigate();

  const socket = useContext(SocketContext);
  const [unreadNotification, setUnreadNotification] = useState(0);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [pagination, setPagination] = useState<IChatbotAPIPagination>(initialPagination);
  const [page, setPage] = useState(1);

  // Init socket to manager notificationCount
  const increaseUnreadNotification = useCallback(() => {
    setUnreadNotification((currentUnreadNotification) => currentUnreadNotification + 1);
  }, []);

  useEffect(() => {
    socket.on('create:notification', increaseUnreadNotification);
    return () => {
      socket.off('create:notification');
    };
  });

  // Retrieve unread notifications count only once
  useEffect(() => {
    const fetchData = async () => {
      if (userId) {
        const response = await notificationService.countUnreadNotifications(userId);
        const { unreadNotificationCount } = response.data;
        setUnreadNotification(unreadNotificationCount);
      }
    };

    fetchData();
  }, [userId]);

  const FetchAndUpdateNotificationWithPage = async () => {
    // page is the value of the next page
    if (page === pagination.pageCount + 1) {
      return;
    }

    setLoading(true);
    const response = await notificationService.listNotifications(page);
    setPage((oldPage) => oldPage + 1);
    setNotifications((oldNotifications) => [...oldNotifications, ...response.data]);
    setPagination(response.pagination);
    setLoading(false);
  };

  const handleShow = async () => {
    setUnreadNotification(0);
    setShow(true);

    if (unreadNotification !== 0) {
      managerService.modifyMetadataReadingDate(userId as string);
    }

    await FetchAndUpdateNotificationWithPage();
  };

  const handleClose = () => {
    setShow(false);
    setPage(1);
    setNotifications([]);
  };

  const handleNotificationClick = (notification: INotification) => {
    notificationService.createNotificationSeenManager(notification.id, userId as string);
    const unitId = notification.ticket.unit.id;
    const ticketId = notification.ticket.id;
    const url = `/chatbot/${unitId}/tickets/${ticketId}`;
    navigate(url);
    handleClose();
  };

  const handleShowMoreClick = () => {
    FetchAndUpdateNotificationWithPage();
  };

  return (
    <>
      <NotificationIcon unreadNotification={unreadNotification} onClick={handleShow} />
      <Offcanvas show={show} onHide={handleClose} placement="end" backdrop={false}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Notificaciones</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 border-top">
          <QueryResult loading={loading}>
            <NotificationList
              notifications={notifications}
              onNotificationClick={handleNotificationClick}
              showShowMore={page <= pagination.pageCount}
              showMoreClick={handleShowMoreClick}
            />
          </QueryResult>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Notifications;
